import { supabase } from "./churchSupabaseClient";
import { getStoredChurchOrgUserSession } from "./churchOrgUserAuthService";

const ACCOUNTS_TABLE = "accounts";
const ACCOUNT_MEMBERS_TABLE = "account_members";
const CHURCH_ORG_USERS_TABLE = "church_org_users";
const CHURCH_ORG_USER_PERMISSIONS_TABLE = "church_org_user_permissions";
const PROFILES_TABLE = "profiles";
const ORGANIZATION_BUCKET = "organization-assets";

const ACCOUNT_SELECT =
  "id, name, type, owner_user_id, created_at, invite_code, church_code, logo_url, logo_path, brand_color, address_line_1, address_line_2, city, state_region, postal_code, country, integrations, platform_access_enabled";
const ORG_USER_SELECT =
  "id, account_id, display_name, username, email, role, is_active, last_login_at, metadata, created_at, updated_at";

function getOrganizationStorageKey(type = "") {
  return `oikos.organization.${type || "church"}`;
}

function cacheOrganizationAccount(account, cacheType = account?.type) {
  if (typeof window === "undefined" || !account || !cacheType) return;

  try {
    const storageKey = getOrganizationStorageKey(cacheType);
    const rawExisting = window.localStorage.getItem(storageKey);
    const existing = rawExisting ? JSON.parse(rawExisting) : null;
    const nextAccount = {
      ...existing,
      ...account,
      logo_url: account.logo_url || account.logoUrl || existing?.logo_url || existing?.logoUrl || "",
      logo_path: account.logo_path || existing?.logo_path || "",
    };

    window.localStorage.setItem(storageKey, JSON.stringify(nextAccount));
  } catch (_error) {
    // ignore cache write failures
  }
}

function clearOrganizationAccountCache(type = "") {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(getOrganizationStorageKey(type));
  } catch (_error) {
    // ignore cache clear failures
  }
}

function announceOrganizationThemeChange(mode, account) {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("oikos-theme-change", {
      detail: {
        mode,
        accountId: account?.id || "",
      },
    })
  );
}

function normalizeAccount(row) {
  return row
    ? {
        ...row,
        logo_url: row.logo_url || "",
        logo_path: row.logo_path || "",
        church_code: row.church_code || "",
        brand_color: row.brand_color || "",
        address_line_1: row.address_line_1 || "",
        address_line_2: row.address_line_2 || "",
        city: row.city || "",
        state_region: row.state_region || "",
        postal_code: row.postal_code || "",
        country: row.country || "",
        integrations: row.integrations && typeof row.integrations === "object" ? row.integrations : {},
        platform_access_enabled: row.platform_access_enabled !== false,
        platformAccessEnabled: row.platform_access_enabled !== false,
      }
    : null;
}

function normalizeOrgUser(row) {
  const metadata = row?.metadata && typeof row.metadata === "object" ? row.metadata : {};
  const permissions = row?.permissions && typeof row.permissions === "object"
    ? row.permissions
    : {
        appIds: Array.isArray(row?.app_permissions) ? row.app_permissions : [],
        managementPageIds: Array.isArray(row?.management_page_permissions)
          ? row.management_page_permissions
          : [],
      };

  return row
    ? {
        id: row.id || "",
        accountId: row.account_id || "",
        displayName: row.display_name || "",
        username: row.username || "",
        email: row.email || "",
        role: row.role || "member",
        isActive: row.is_active !== false,
        lastLoginAt: row.last_login_at || "",
        familyId: row.familyId || row.family_id || metadata.familyId || metadata.assignedFamilyId || "",
        metadata,
        permissions: {
          appIds: Array.isArray(permissions.appIds) ? permissions.appIds : [],
          managementPageIds: Array.isArray(permissions.managementPageIds)
            ? permissions.managementPageIds
            : [],
        },
        createdAt: row.created_at || "",
        updatedAt: row.updated_at || "",
      }
    : null;
}

function getOrgAdminSession(accountId = "") {
  const session = getStoredChurchOrgUserSession();
  const sessionAccountId = session?.account?.id || session?.orgUser?.account_id || session?.orgUser?.accountId || "";
  const role = String(session?.orgUser?.role || "").toLowerCase();

  if (!session?.sessionToken || role !== "admin") return null;
  if (accountId && sessionAccountId && sessionAccountId !== accountId) return null;

  return session;
}

function buildAccountUpdatePayload(updates = {}) {
  const payload = {};

  if (typeof updates.name === "string") {
    payload.name = updates.name.trim();
  }

  if (typeof updates.logoUrl === "string") {
    payload.logo_url = updates.logoUrl;
  }

  if (typeof updates.logoPath === "string") {
    payload.logo_path = updates.logoPath;
  }

  if (typeof updates.brandColor === "string") {
    payload.brand_color = updates.brandColor.trim();
  }

  if (typeof updates.addressLine1 === "string") {
    payload.address_line_1 = updates.addressLine1.trim();
  }

  if (typeof updates.addressLine2 === "string") {
    payload.address_line_2 = updates.addressLine2.trim();
  }

  if (typeof updates.city === "string") {
    payload.city = updates.city.trim();
  }

  if (typeof updates.stateRegion === "string") {
    payload.state_region = updates.stateRegion.trim();
  }

  if (typeof updates.postalCode === "string") {
    payload.postal_code = updates.postalCode.trim();
  }

  if (typeof updates.country === "string") {
    payload.country = updates.country.trim();
  }

  if (typeof updates.integrations === "object" && updates.integrations !== null) {
    payload.integrations = updates.integrations;
  }

  return payload;
}

async function fetchMembers(accountId) {
  const { data: memberRows, error: memberError } = await supabase
    .from(ACCOUNT_MEMBERS_TABLE)
    .select("account_id, user_id, role, status")
    .eq("account_id", accountId);

  if (memberError) throw memberError;

  const members = memberRows || [];
  if (members.length === 0) return [];

  const userIds = members.map((member) => member.user_id);
  const { data: profiles, error: profileError } = await supabase
    .from(PROFILES_TABLE)
    .select("id, full_name, email")
    .in("id", userIds);

  if (profileError) throw profileError;

  const profileMap = new Map((profiles || []).map((profile) => [profile.id, profile]));

  return members.map((member) => {
    const profile = profileMap.get(member.user_id);

    return {
      userId: member.user_id,
      role: member.role || "member",
      status: member.status || "pending",
      fullName: profile?.full_name || profile?.email || "Unnamed User",
      email: profile?.email || "",
    };
  });
}

async function findChurchAccount(userId) {
  const { data: ownedAccounts, error: ownedError } = await supabase
    .from(ACCOUNTS_TABLE)
    .select(ACCOUNT_SELECT)
    .eq("owner_user_id", userId)
    .eq("type", "church")
    .order("created_at", { ascending: true })
    .limit(1);

  if (ownedError) throw ownedError;
  if (ownedAccounts?.[0]) {
    return {
      account: normalizeAccount(ownedAccounts[0]),
      isOwner: true,
      membership: {
        role: "owner",
        status: "active",
      },
    };
  }

  const { data: memberships, error: membershipError } = await supabase
    .from(ACCOUNT_MEMBERS_TABLE)
    .select("account_id, role, status")
    .eq("user_id", userId);

  if (membershipError) throw membershipError;

  const accountIds = (memberships || []).map((item) => item.account_id);
  if (accountIds.length === 0) {
    return { account: null, isOwner: false, membership: null };
  }

  const { data: accounts, error: accountsError } = await supabase
    .from(ACCOUNTS_TABLE)
    .select(ACCOUNT_SELECT)
    .in("id", accountIds)
    .eq("type", "church")
    .order("created_at", { ascending: true })
    .limit(1);

  if (accountsError) throw accountsError;

  const account = normalizeAccount(accounts?.[0]);
  const membership = (memberships || []).find((item) => item.account_id === account?.id) || null;

  return {
    account,
    isOwner: false,
    membership,
  };
}

async function fetchAccountById(accountId) {
  const { data, error } = await supabase
    .from(ACCOUNTS_TABLE)
    .select(ACCOUNT_SELECT)
    .eq("id", accountId)
    .maybeSingle();

  if (error) throw error;

  return normalizeAccount(data);
}

export async function fetchOrganizationAccess(userId) {
  const accountType = "church";
  const orgUserSession = getStoredChurchOrgUserSession();

  if (orgUserSession?.orgUser?.id && orgUserSession.orgUser.id === userId) {
    const account = normalizeAccount(orgUserSession.account);
    if (account?.platformAccessEnabled === false) {
      clearChurchOrgUserSession();
      clearOrganizationAccountCache(accountType);
      return {
        accountType,
        account: null,
        members: [],
        isOwner: false,
        membership: null,
      };
    }

    cacheOrganizationAccount(account, accountType);
    announceOrganizationThemeChange(accountType, account);

    return {
      accountType,
      account,
      members: [
        {
          userId: orgUserSession.orgUser.id,
          role: orgUserSession.orgUser.role || "member",
          status: "active",
          fullName: orgUserSession.orgUser.display_name || orgUserSession.orgUser.username || "Church User",
          email: orgUserSession.orgUser.email || "",
        },
      ],
      isOwner: false,
      membership: {
        role: orgUserSession.orgUser.role || "member",
        status: "active",
      },
    };
  }

  if (!userId) {
    clearOrganizationAccountCache(accountType);
    return {
      accountType,
      account: null,
      members: [],
      isOwner: false,
      membership: null,
    };
  }

  const { account, isOwner, membership } = await findChurchAccount(userId);

  if (!account?.id) {
    clearOrganizationAccountCache(accountType);
    return {
      accountType,
      account: null,
      members: [],
      isOwner: false,
      membership: null,
    };
  }

  if (account.platformAccessEnabled === false) {
    clearOrganizationAccountCache(accountType);
    return {
      accountType,
      account: null,
      members: [],
      isOwner: false,
      membership: null,
    };
  }

  cacheOrganizationAccount(account, accountType);
  announceOrganizationThemeChange(accountType, account);

  return {
    accountType,
    account,
    members: await fetchMembers(account.id),
    isOwner,
    membership,
  };
}

export async function fetchOrganizationBranding(userId) {
  const access = await fetchOrganizationAccess(userId);
  return access?.account || null;
}

export async function updateOrganizationSettings(accountId, updates = {}) {
  const payload = buildAccountUpdatePayload(updates);
  const orgAdminSession = getOrgAdminSession(accountId);

  if (orgAdminSession?.sessionToken) {
    const { data, error } = await supabase.rpc("church_org_admin_update_organization_settings", {
      p_session_token: orgAdminSession.sessionToken,
      p_updates: payload,
    });

    if (error) throw error;

    const account = normalizeAccount(Array.isArray(data) ? data[0] : data) || await fetchAccountById(accountId);
    if (!account) {
      throw new Error("Organization was updated, but the updated account could not be reloaded.");
    }

    cacheOrganizationAccount(account, "church");
    announceOrganizationThemeChange("church", account);
    return account;
  }

  const { data, error } = await supabase
    .from(ACCOUNTS_TABLE)
    .update(payload)
    .eq("id", accountId)
    .select(ACCOUNT_SELECT)
    .maybeSingle();

  if (error) throw error;

  const account = normalizeAccount(data) || await fetchAccountById(accountId);
  if (!account) {
    throw new Error("Organization was updated, but the updated account could not be reloaded.");
  }

  cacheOrganizationAccount(account, "church");
  announceOrganizationThemeChange("church", account);
  return account;
}

export async function uploadOrganizationLogo({ account, file }) {
  if (!account?.id) {
    throw new Error("Missing organization account.");
  }

  const safeName = file.name.replace(/\s+/g, "-").toLowerCase();
  const filePath = `${account.id}/${Date.now()}-${safeName}`;

  const { error: uploadError } = await supabase.storage
    .from(ORGANIZATION_BUCKET)
    .upload(filePath, file, {
      cacheControl: "3600",
      upsert: false,
    });

  if (uploadError) throw uploadError;

  const {
    data: { publicUrl },
  } = supabase.storage.from(ORGANIZATION_BUCKET).getPublicUrl(filePath);

  if (account.logo_path) {
    await supabase.storage
      .from(ORGANIZATION_BUCKET)
      .remove([account.logo_path]);
  }

  return updateOrganizationSettings(account.id, {
    logoUrl: publicUrl,
    logoPath: filePath,
  });
}

export async function uploadOrganizationAsset({ account, file, folder = "assets" }) {
  if (!account?.id) {
    throw new Error("Missing organization account.");
  }

  if (!file) {
    throw new Error("Choose a file to upload.");
  }

  const safeFolder = String(folder || "assets").replace(/[^a-z0-9-]/gi, "-").toLowerCase();
  const safeName = String(file.name || "asset")
    .replace(/[^a-z0-9._-]/gi, "-")
    .replace(/-+/g, "-")
    .toLowerCase();
  const filePath = `${account.id}/${safeFolder}/${Date.now()}-${safeName}`;

  const { error: uploadError } = await supabase.storage
    .from(ORGANIZATION_BUCKET)
    .upload(filePath, file, {
      cacheControl: "3600",
      upsert: false,
    });

  if (uploadError) throw uploadError;

  const {
    data: { publicUrl },
  } = supabase.storage.from(ORGANIZATION_BUCKET).getPublicUrl(filePath);

  return {
    publicUrl,
    filePath,
  };
}

export async function fetchChurchOrgUsers(accountId) {
  if (!accountId) return [];

  const orgAdminSession = getOrgAdminSession(accountId);
  if (orgAdminSession?.sessionToken) {
    const { data, error } = await supabase.rpc("church_org_admin_list_org_users", {
      p_session_token: orgAdminSession.sessionToken,
    });

    if (error) throw error;

    return (data || []).map((row) => normalizeOrgUser(row)).filter(Boolean);
  }

  const { data, error } = await supabase
    .from(CHURCH_ORG_USERS_TABLE)
    .select(ORG_USER_SELECT)
    .eq("account_id", accountId)
    .order("display_name", { ascending: true });

  if (error) throw error;

  const rows = data || [];
  const userIds = rows.map((row) => row.id).filter(Boolean);
  const permissionMap = new Map(
    userIds.map((id) => [id, { appIds: [], managementPageIds: [] }])
  );

  if (userIds.length > 0) {
    const { data: permissions, error: permissionsError } = await supabase
      .from(CHURCH_ORG_USER_PERMISSIONS_TABLE)
      .select("org_user_id, permission_group, permission_key, is_enabled")
      .eq("account_id", accountId)
      .in("org_user_id", userIds)
      .eq("is_enabled", true);

    if (permissionsError) throw permissionsError;

    (permissions || []).forEach((permission) => {
      const access = permissionMap.get(permission.org_user_id);
      if (!access) return;

      if (permission.permission_group === "app") {
        access.appIds.push(permission.permission_key);
      } else if (permission.permission_group === "management_page") {
        access.managementPageIds.push(permission.permission_key);
      }
    });
  }

  return rows
    .map((row) => normalizeOrgUser({ ...row, permissions: permissionMap.get(row.id) }))
    .filter(Boolean);
}

export async function saveChurchOrgUser({
  accountId,
  userId = null,
  displayName = "",
  username = "",
  password = "",
  email = "",
  role = "member",
  isActive = true,
  metadata = {},
  appPermissions = [],
  managementPagePermissions = [],
}) {
  const orgAdminSession = getOrgAdminSession(accountId);
  if (orgAdminSession?.sessionToken) {
    const { data, error } = await supabase.rpc("church_org_admin_save_org_user", {
      p_session_token: orgAdminSession.sessionToken,
      p_user_id: userId,
      p_display_name: displayName,
      p_username: username,
      p_password: password,
      p_email: email,
      p_role: role,
      p_is_active: isActive,
      p_app_permissions: appPermissions,
      p_management_page_permissions: managementPagePermissions,
      p_metadata: metadata,
    });

    if (error) throw error;

    return normalizeOrgUser(Array.isArray(data) ? data[0] : data);
  }

  const { data, error } = await supabase.rpc("church_admin_save_org_user", {
    p_account_id: accountId,
    p_user_id: userId,
    p_display_name: displayName,
    p_username: username,
    p_password: password,
    p_email: email,
    p_role: role,
    p_is_active: isActive,
    p_app_permissions: appPermissions,
    p_management_page_permissions: managementPagePermissions,
    p_metadata: metadata,
  });

  if (error) throw error;

  return normalizeOrgUser(Array.isArray(data) ? data[0] : data);
}

export async function deleteChurchOrgUser(accountId, userId) {
  const orgAdminSession = getOrgAdminSession(accountId);
  if (orgAdminSession?.sessionToken) {
    const { error } = await supabase.rpc("church_org_admin_delete_org_user", {
      p_session_token: orgAdminSession.sessionToken,
      p_user_id: userId,
    });

    if (error) throw error;
    return;
  }

  const { error } = await supabase
    .from(CHURCH_ORG_USERS_TABLE)
    .delete()
    .eq("account_id", accountId)
    .eq("id", userId);

  if (error) throw error;
}
